import { useEffect } from 'react';
import { useLocation, matchRoutes } from 'react-router';
import { useDispatch, useStore, shallowEqual, useSelector } from 'react-redux';
import { ALL_PRIVATE_ROUTES } from '@app/router';
import { replaceBreadcrumbs } from 'redux_store/slices/navigationSlice';
import { selectBreadcrumbs } from 'redux_store/selectors/navigationSelectors';
import { computeNextStack } from './computeNextStack';
import { buildParentChain } from './buildParentChain';
import { resolveBreadcrumb } from './resolveBreadcrumb';
import { findRouteByTemplate } from './findRouteByTemplate';

/**
 * Find the route in ALL_PRIVATE_ROUTES whose template matches the
 * current pathname, using react-router's own specificity ranking
 * (static segments beat dynamic params).
 *
 * The previous first-match loop depended on registration order and
 * picked the WRONG route when templates overlap: the dashboard
 * template `/:cua/patients/:puid/:tableName/` matches the edit URL
 * `/:cua/patients/:puid/edit` (tableName = "edit") and is registered
 * first — so the edit page was treated as a dashboard tab switch, its
 * `replaceSibling` collapsed the dashboard crumb into a self-link and
 * the trail lost the way back to the dashboard entirely.
 */
const matchableRoutes = ALL_PRIVATE_ROUTES.filter(r => r?.path).map(r => ({
  path: r.path,
  appRoute: r,
}));

const findRouteByPathname = pathname => {
  const matched = matchRoutes(matchableRoutes, pathname);
  return matched?.[0]?.route?.appRoute || null;
};

/**
 * Single mount-point that bridges react-router (URL) and Redux
 * (breadcrumbs stack). Mount in widgets/app-shell/ui/AppShell.jsx
 * exactly once per session.
 *
 * Reads location.state?.breadcrumbReset to support the "profile menu
 * opens edit-account as a fresh root" override (Task 9).
 */
export const useBreadcrumbsSync = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const currentStack = useSelector(selectBreadcrumbs, shallowEqual);

  useEffect(() => {
    const route = findRouteByPathname(location.pathname);
    if (!route || !route.additional?.breadcrumb) {
      if (currentStack.length !== 0) dispatch(replaceBreadcrumbs([]));
      return;
    }

    const entry = resolveBreadcrumb(route, location.pathname);
    if (!entry) {
      if (currentStack.length !== 0) dispatch(replaceBreadcrumbs([]));
      return;
    }

    const cfg = route.additional.breadcrumb;
    const forceRoot = !!location.state?.breadcrumbReset;
    const effectiveEntry = forceRoot ? { ...entry, isRoot: true } : entry;
    const effectiveCfg = forceRoot ? { ...cfg, isRoot: true } : cfg;

    const parentChain =
      currentStack.length === 0 && !effectiveEntry.isRoot
        ? buildParentChain(effectiveEntry.pathTemplate, effectiveEntry.params, findRouteByTemplate)
        : [];

    const next = computeNextStack(currentStack, effectiveEntry, effectiveCfg, parentChain);

    if (!stacksEqual(currentStack, next)) {
      dispatch(replaceBreadcrumbs(next));
    }
    // currentStack omitted from deps on purpose — we only resync on URL change.
    // The selector in the next render reflects any dispatch from this run.
    // eslint-disable-next-line
  }, [location.pathname, location.state, dispatch, store]);
};

const stacksEqual = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].resolvedPath !== b[i].resolvedPath) return false;
    if (a[i].pathTemplate !== b[i].pathTemplate) return false;
    if (a[i].isRoot !== b[i].isRoot) return false;
  }
  return true;
};
